
import { useState, useEffect } from 'react';

export interface BookingState {
  selectedProvider: string;
  selectedDate: Date | undefined;
  selectedTime: string;
  patientInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    reason: string;
  };
}

const STORAGE_KEY = 'booking-draft';

const initialState: BookingState = {
  selectedProvider: '',
  selectedDate: undefined,
  selectedTime: '',
  patientInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    reason: '',
  },
};

export function useBookingState() {
  const [bookingState, setBookingState] = useState<BookingState>(initialState);
  const [isLoading, setIsLoading] = useState(true);

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert date string back to Date object
        if (parsed.selectedDate) {
          parsed.selectedDate = new Date(parsed.selectedDate);
        }
        setBookingState(parsed);
      }
    } catch (error) {
      console.log('Error loading booking draft:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (!isLoading) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(bookingState));
      } catch (error) {
        console.log('Error saving booking draft:', error);
      }
    }
  }, [bookingState, isLoading]);

  const updateProvider = (providerId: string) => {
    setBookingState(prev => ({ ...prev, selectedProvider: providerId }));
  };

  const updateDateTime = (date: Date | undefined, time: string) => {
    setBookingState(prev => ({ 
      ...prev, 
      selectedDate: date, 
      selectedTime: time 
    }));
  };

  const updatePatientInfo = (info: Partial<BookingState['patientInfo']>) => {
    setBookingState(prev => ({
      ...prev,
      patientInfo: { ...prev.patientInfo, ...info }
    }));
  };

  const clearBooking = () => {
    setBookingState(initialState);
    localStorage.removeItem(STORAGE_KEY);
  };

  const hasDraft = () => {
    return bookingState.selectedProvider !== '' || 
           bookingState.selectedDate !== undefined || 
           bookingState.selectedTime !== '' ||
           Object.values(bookingState.patientInfo).some(value => value !== '');
  };

  return {
    bookingState,
    isLoading,
    updateProvider,
    updateDateTime,
    updatePatientInfo,
    clearBooking,
    hasDraft,
  };
}
